/**
 * A ZIP writer, because an .xlsx IS a zip of XML files.
 *
 * ── Why STORED and not DEFLATED ────────────────────────────────────────────
 * Every entry is written uncompressed (method 0). The reason is that this code
 * has to run in TWO places with no shared compression primitive: the browser,
 * where the report screens build the file from what is on screen, and Node,
 * where the suite reads it back and a worker will one day write it. Node has
 * `zlib.deflateRawSync`; the browser has `CompressionStream`, which is async
 * and would make `buildXlsx` return a promise for a platform reason rather
 * than a domain one. STORED needs neither, is synchronous, is deterministic,
 * and is a perfectly legal zip.
 *
 * The cost is size: XML compresses roughly 5-10×, so a 5,000-row report is a
 * few hundred kilobytes instead of a few tens. That is a download, once,
 * already on a page that fetched more than that. If it ever matters, one
 * function changes — `entries()` takes the compressed bytes and a method
 * number, and nothing above here moves.
 *
 * ── What is deliberately not implemented ───────────────────────────────────
 * ZIP64. It caps the archive at 4 GB and 65,535 entries; this writer produces
 * six entries and a bounded grid. Data descriptors, encryption, and the
 * unicode path extra field are likewise absent — every filename here is ASCII
 * and fixed by the OOXML spec.
 */
const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1)
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        table[n] = c >>> 0;
    }
    return table;
})();
export function crc32(bytes) {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    }
    return (c ^ 0xffffffff) >>> 0;
}
/**
 * A fixed DOS timestamp — 1980-01-01 00:00, the zero of the format.
 *
 * DELIBERATELY NOT `now()`. A build that embeds the clock produces a different
 * file every time it runs, which means a test cannot assert on the bytes and
 * two identical reports do not hash the same. Nobody reads the mtime inside an
 * .xlsx; Excel shows the file's own date.
 */
const DOS_TIME = 0;
const DOS_DATE = 0x0021; // 1980-01-01
export function zip(entries) {
    const encoder = new TextEncoder();
    const locals = [];
    const centrals = [];
    let offset = 0;
    for (const entry of entries) {
        const name = encoder.encode(entry.name);
        const data = encoder.encode(entry.content);
        const sum = crc32(data);
        const local = new Uint8Array(30 + name.length + data.length);
        const localView = new DataView(local.buffer);
        localView.setUint32(0, 0x04034b50, true); // local file header signature
        localView.setUint16(4, 20, true); // version needed: 2.0
        localView.setUint16(6, 0x0800, true); // flags: bit 11 — the name is UTF-8
        localView.setUint16(8, 0, true); // method: STORED
        localView.setUint16(10, DOS_TIME, true);
        localView.setUint16(12, DOS_DATE, true);
        localView.setUint32(14, sum, true);
        localView.setUint32(18, data.length, true); // compressed size
        localView.setUint32(22, data.length, true); // uncompressed size
        localView.setUint16(26, name.length, true);
        localView.setUint16(28, 0, true); // extra field length
        local.set(name, 30);
        local.set(data, 30 + name.length);
        locals.push(local);
        const central = new Uint8Array(46 + name.length);
        const centralView = new DataView(central.buffer);
        centralView.setUint32(0, 0x02014b50, true); // central directory signature
        centralView.setUint16(4, 20, true); // version made by
        centralView.setUint16(6, 20, true); // version needed
        centralView.setUint16(8, 0x0800, true);
        centralView.setUint16(10, 0, true); // STORED
        centralView.setUint16(12, DOS_TIME, true);
        centralView.setUint16(14, DOS_DATE, true);
        centralView.setUint32(16, sum, true);
        centralView.setUint32(20, data.length, true);
        centralView.setUint32(24, data.length, true);
        centralView.setUint16(28, name.length, true);
        centralView.setUint16(30, 0, true); // extra
        centralView.setUint16(32, 0, true); // comment
        centralView.setUint16(34, 0, true); // disk number
        centralView.setUint16(36, 0, true); // internal attributes
        centralView.setUint32(38, 0, true); // external attributes
        centralView.setUint32(42, offset, true); // offset of the local header
        central.set(name, 46);
        centrals.push(central);
        offset += local.length;
    }
    const centralSize = centrals.reduce((total, part) => total + part.length, 0);
    const end = new Uint8Array(22);
    const endView = new DataView(end.buffer);
    endView.setUint32(0, 0x06054b50, true); // end of central directory
    endView.setUint16(8, entries.length, true); // entries on this disk
    endView.setUint16(10, entries.length, true); // entries total
    endView.setUint32(12, centralSize, true);
    endView.setUint32(16, offset, true); // offset of the central directory
    return concat([...locals, ...centrals, end]);
}
function concat(parts) {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(total);
    let at = 0;
    for (const part of parts) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}
//# sourceMappingURL=zip.js.map